"use client";

import {
  isLazyToolOutput,
  LAZY_TOOL_OUTPUT_KEY,
  type LazyToolOutput,
} from "@bg-bgi/magnus-sdk";
import { partTypeToToolName, useToolRegistry } from "@bg-bgi/magnus-sdk/react";
import { Skeleton } from "@workspace/ui/components/skeleton";
import { cn } from "@workspace/ui/lib/utils";
import { Wrench } from "lucide-react";
import { Suspense, use, useLayoutEffect, useRef } from "react";
import {
  type LazyToolOutputResolver,
  useLazyToolOutputResolver,
} from "./lazy-tool-output";
import { useExtraToolViews } from "./tool-views";
import { defineToolView } from "./tools/_framework/define-tool-view";
import { TimelineContext, toolIconClass } from "./tools/_framework/tool-item";

/**
 * Structural view of an AI SDK tool part (`tool-*` / `dynamic-tool`).
 * Mirrors the detection in `@bg-bgi/magnus-sdk/react`'s tool registry:
 * static tool parts carry the tool name in the `type` (`tool-bash` → `bash`),
 * dynamic ones carry it in `toolName`.
 */
export interface ToolPartLike {
  errorText?: string;
  input?: unknown;
  output?: unknown;
  result?: unknown;
  state?: string;
  toolCallId?: string;
  toolName?: string;
  type: string;
}

export function isToolPart(part: { type: string }): part is ToolPartLike {
  return part.type === "dynamic-tool" || part.type.startsWith("tool-");
}

/** The tool name a {@link ToolPartLike} was called as. */
export function toolPartName(part: ToolPartLike): string {
  return part.type === "dynamic-tool" && part.toolName
    ? part.toolName
    : partTypeToToolName(part.type);
}

function safeStringify(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    return String(value);
  }
}

/**
 * Turn a serialized tool type (`tool-${name}` or `${cap}_${toolName}`)
 * into a human-readable label. MCP / progressive-disclosure tools use a
 * `${capabilityId}_${toolName}` convention; we split on the first
 * underscore and present as `Capability · toolName`.
 *
 * `documentSearch` (camelCase, no underscore prefix) becomes
 * `Document Search`. `notion_search` becomes `Notion · search`.
 */
function humanizeToolName(type: string): string {
  const raw = type.startsWith("tool-") ? type.slice("tool-".length) : type;
  const firstUnderscore = raw.indexOf("_");
  if (firstUnderscore > 0) {
    const capPart = raw.slice(0, firstUnderscore);
    const toolPart = raw.slice(firstUnderscore + 1);
    return `${capitalize(capPart)} · ${toolPart}`;
  }
  // Split camelCase / PascalCase into spaced words: "documentSearch" →
  // "document Search" → "Document Search".
  const spaced = raw.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  return spaced
    .split(" ")
    .map((part) => capitalize(part))
    .join(" ");
}

function capitalize(value: string): string {
  if (!value) {
    return value;
  }
  return value[0]?.toUpperCase() + value.slice(1);
}

/**
 * Soft-failure convention: `output.ok === false` (or `output.error`) marks
 * the error tier without the AI SDK's `output-error` state, which many
 * servers never emit. Mirrors the web app's `isOkEnvelopeError` ladder.
 */
const getSoftError = (output: unknown): string | undefined => {
  if (!output || typeof output !== "object") {
    return undefined;
  }
  const record = output as Record<string, unknown>;
  if (record.ok === false) {
    return typeof record.error === "string"
      ? record.error
      : record.error == null
        ? "Tool call failed"
        : safeStringify(record.error);
  }
  return typeof record.error === "string" ? record.error : undefined;
};

function ToolIoBlock({
  label,
  tone = "default",
  children,
}: {
  label: string;
  tone?: "default" | "error";
  children: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span
        className={cn(
          "font-medium text-[10px] uppercase tracking-wide",
          tone === "error" ? "text-destructive" : "text-muted-foreground/70"
        )}
      >
        {label}
      </span>
      <pre
        className={cn(
          "max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md p-2 font-mono text-[11px] leading-relaxed",
          tone === "error"
            ? "bg-destructive/10 text-destructive"
            : "bg-muted/50 text-muted-foreground"
        )}
      >
        {children}
      </pre>
    </div>
  );
}

/**
 * Universal generic fallback for any tool without a registered UI — the
 * pre-block `GenericToolView` rebuilt on the block's `defineToolView`
 * ladder: a shimmering label while the tool runs, then a compact row that
 * expands to the raw input/output JSON. The block's soft-error envelope
 * (`output.ok === false` / bare `output.error`) rides the ladder's
 * `softError` slot so soft failures render the error tier too.
 */
const GenericToolView = defineToolView({
  icon: <Wrench className={toolIconClass} />,
  // Only shimmer while the tool is genuinely still streaming input. Stale
  // persisted parts (interrupted before output ever arrived, state stuck at
  // `input-available`) must render a static row rather than an animated
  // shimmer implying live activity.
  isLoading: (snap) =>
    snap.state === "input-streaming" || snap.state === "input-available",
  loading: (snap) => humanizeToolName(snap.type),
  done: (snap) => humanizeToolName(snap.type),
  softError: (snap) => getSoftError(snap.output),
});

/**
 * Placeholder while a lazy tool-view chunk streams in. Standalone tools
 * render cards, so the fallback reserves ~one card row (keeps the message
 * column from collapsing/jumping on first open). Inside a connector
 * timeline the card shape looks foreign between rows, so the fallback
 * mirrors the row instead — same h-7, same icon gutter, same label offset —
 * and registers with the timeline so the connector line paints through the
 * loading row instead of appearing after.
 */
function ToolViewFallback() {
  const timeline = use(TimelineContext);
  const inTimeline = !!timeline?.isInTimeline;

  const registerRef = useRef(timeline?.register);
  registerRef.current = timeline?.register;
  useLayoutEffect(() => {
    registerRef.current?.();
  }, []);

  if (!inTimeline) {
    return (
      <div className="my-2">
        <Skeleton className="h-9 w-full max-w-md rounded-md" />
      </div>
    );
  }

  return (
    <div className="flex h-7 min-w-0 items-center">
      <div className="flex w-5 shrink-0 flex-col items-center justify-center">
        <Skeleton className="size-3.5 rounded-full" />
      </div>
      <div className="flex min-w-0 flex-1 items-center pl-1.5">
        <Skeleton className="h-3 w-28 rounded" />
      </div>
    </div>
  );
}

/**
 * A tool part whose `output` arrived as a lazy placeholder (the chat read
 * opted into `lazyToolOutputs`). Suspends on the host's resolver, then
 * renders the normal `ToolPart` with the real output, so registered views
 * never see the placeholder. A failed resolve degrades to the generic row
 * with a soft error rather than handing a view an output shape it never
 * expects; without a resolver the generic row shows the placeholder's
 * surviving envelope fields.
 */
function LazyToolPart({
  part,
  streaming,
}: {
  part: ToolPartLike;
  streaming: boolean;
}) {
  const resolver = useLazyToolOutputResolver();
  const { [LAZY_TOOL_OUTPUT_KEY]: ref, ...envelope } =
    part.output as LazyToolOutput;

  if (!resolver) {
    return <GenericToolView invocation={{ ...part, output: envelope }} />;
  }
  return (
    <Suspense fallback={<ToolViewFallback />}>
      <ResolvedToolPart
        part={part}
        resolve={resolver.resolve}
        streaming={streaming}
        toolCallId={ref.toolCallId}
      />
    </Suspense>
  );
}

function ResolvedToolPart({
  part,
  resolve,
  streaming,
  toolCallId,
}: {
  part: ToolPartLike;
  resolve: LazyToolOutputResolver["resolve"];
  streaming: boolean;
  toolCallId: string;
}) {
  const result = use(resolve(toolCallId));
  if (!result.ok) {
    return (
      <GenericToolView
        invocation={{
          ...part,
          output: { ok: false, error: "Couldn't load this tool's output" },
        }}
      />
    );
  }
  // A resolved output is never itself a placeholder, so this recursion
  // terminates in the registered-view branch below.
  return (
    <ToolPart part={{ ...part, output: result.output }} streaming={streaming} />
  );
}

/**
 * Renders a tool invocation. Views registered via `defineClientTool`
 * (through `useToolRegistry`) or the host's `toolViews` seam win; anything
 * else gets the universal generic row (see {@link GenericToolView}).
 */
export function ToolPart({
  part,
  streaming = false,
}: {
  part: ToolPartLike;
  /** True while the owning assistant message is still streaming. */
  streaming?: boolean;
}) {
  const registry = useToolRegistry(useExtraToolViews());

  if (isLazyToolOutput(part.output)) {
    return <LazyToolPart part={part} streaming={streaming} />;
  }
  const toolName = toolPartName(part);

  // Views derive their labels from `invocation.type` (the static
  // `tool-${name}` convention — e.g. the MCP ladder strips the capability
  // prefix off `type` to phrase its verb). A `dynamic-tool` part carries the
  // name in `toolName` and a bare "dynamic-tool" type, so hand views a
  // normalized invocation — the same shape the server's persist-time rewrite
  // produces. Without this every dynamic/MCP row renders as "Dynamic-tool".
  const invocation: ToolPartLike =
    part.type === "dynamic-tool" && part.toolName
      ? { ...part, type: `tool-${part.toolName}` }
      : part;

  const registered = registry[toolName];
  if (registered) {
    const View = registered.view;
    return (
      <Suspense fallback={<ToolViewFallback />}>
        <View invocation={invocation} />
      </Suspense>
    );
  }

  // `streaming` is currently informational — the generic ladder infers
  // loading from the part state, and settled-but-pending parts are
  // intercepted upstream (hidden solo / "Cancelled" in a group).
  return <GenericToolView invocation={invocation} />;
}

// Re-exported for the extracted tool-view ladder (`tools/_framework`) —
// its auto-JSON fallback renders these same blocks (plan 030).
export { safeStringify, ToolIoBlock };
